import React, { useMemo, useSyncExternalStore } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { bannerTitleIsSecondary, isPageBannerHidden, resolvePageInfo } from '../utils/routePageMeta';
import { resolveNavTopicIconForPath } from '../utils/resolveNavTopicIcon';
import {
  getMsmeRegistrationSuccessBannerHidden,
  shouldHidePageBannerForMsmeRegistrationSuccess,
  subscribeMsmeRegistrationSuccessBannerHidden,
} from '../utils/msmeRegistrationSuccessBanner';

interface PageInformationBannerProps {
  compact?: boolean;
}

const PageInformationBanner: React.FC<PageInformationBannerProps> = ({ compact = false }) => {
  const location = useLocation();
  const pathname = location.pathname || '/';
  const search = location.search || '';
  // Subscribe so success flag flips re-render; pathname scopes the hide decision.
  useSyncExternalStore(
    subscribeMsmeRegistrationSuccessBannerHidden,
    getMsmeRegistrationSuccessBannerHidden,
    () => false
  );
  const hideForMsmeRegistrationSuccess = shouldHidePageBannerForMsmeRegistrationSuccess(pathname);

  const pageInfo = useMemo(() => resolvePageInfo(pathname), [pathname]);
  const titleIsSecondary = bannerTitleIsSecondary(pathname);
  const topicIcon = useMemo(
    () => resolveNavTopicIconForPath(pathname, search),
    [pathname, search]
  );

  if (isPageBannerHidden(pathname) || hideForMsmeRegistrationSuccess) {
    return null;
  }

  return (
    <Box
      component="section"
      aria-label={pageInfo.title}
      sx={{
        mb: compact ? 0 : 0.5,
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="flex-start"
        sx={{ p: compact ? 1.25 : 1.5, flex: 1, minWidth: 0 }}
      >
        <Box
          aria-hidden
          sx={{
            mt: 0.25,
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: 'action.hover',
            flexShrink: 0,
          }}
        >
          {topicIcon}
        </Box>
        <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component={titleIsSecondary ? 'p' : 'h1'}
            variant={compact ? 'subtitle1' : 'h6'}
            sx={{ fontWeight: 700, letterSpacing: 0.1, lineHeight: 1.3 }}
          >
            {pageInfo.title}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.45, maxWidth: { md: '72ch' } }}
          >
            {pageInfo.pageDescription}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

export default PageInformationBanner;
